import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Card, CardBody, Chip } from '@nextui-org/react';
import {
  Shield,
  Sparkles,
  Dices,
  Sliders,
  CalendarDays,
  Link2,
  ChevronRight,
  Smartphone,
  Trophy,
  FileText,
  BarChart3,
  BookOpen,
} from 'lucide-react';
import adminNewsAutomationService from '../../services/adminNewsAutomationService';

const ACCENT = '#3fced1';

interface PlaceholderModule {
  name: string;
  description: string;
  icon: React.ComponentType<{ size?: string | number; className?: string }>;
}

const placeholderModules: PlaceholderModule[] = [
  {
    name: 'Kalender-Quellen',
    description: 'Event-Quellen konfigurieren',
    icon: CalendarDays,
  },
  {
    name: 'MSS-Verknüpfungen',
    description: 'MSS-Serien & -Spieler verknüpfen',
    icon: Link2,
  },
];

const AdminDashboardPage: React.FC = () => {
  const [pendingCount, setPendingCount] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    adminNewsAutomationService
      .getPendingArticles({ limit: 1, offset: 0 })
      .then((res) => {
        if (!cancelled) setPendingCount(res.total);
      })
      .catch(() => {
        /* Badge einfach nicht anzeigen */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Admin-Bereich | DartCloud</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="w-full">
        {/* Hero */}
        <section className="border-b border-zinc-800 bg-gradient-to-br from-zinc-900 to-zinc-950">
          <div className="max-w-[1200px] mx-auto px-4 sm:px-8 py-10 sm:py-12">
            <div className="flex items-center gap-3 mb-3">
              <span
                className="flex h-11 w-11 items-center justify-center rounded-xl"
                style={{ backgroundColor: `${ACCENT}1A`, color: ACCENT }}
              >
                <Shield size={24} />
              </span>
              <h1 className="text-2xl sm:text-4xl font-extrabold text-white leading-tight">
                Admin-Bereich
              </h1>
            </div>
            <p className="text-base text-gray-400 max-w-2xl">
              Zentrale Verwaltung für DartCloud. Wähle ein Modul, um loszulegen.
            </p>
          </div>
        </section>

        {/* Module */}
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8 py-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Aktive Kachel: News-Automation */}
            <RouterLink to="/admin/news-automation" className="group">
              <Card
                isPressable={false}
                className="h-full w-full bg-zinc-900 border border-zinc-800 transition-colors group-hover:border-[#3fced1]/60"
              >
                <CardBody className="p-5">
                  <div className="flex items-start justify-between gap-3">
                    <span
                      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl"
                      style={{ backgroundColor: `${ACCENT}1A`, color: ACCENT }}
                    >
                      <Sparkles size={22} />
                    </span>
                    {pendingCount !== null && pendingCount > 0 && (
                      <Chip
                        size="sm"
                        className="bg-[#3fced1] text-black font-semibold"
                      >
                        {pendingCount} offen
                      </Chip>
                    )}
                  </div>
                  <h2 className="mt-4 text-lg font-bold text-white flex items-center gap-1">
                    News-Automation
                    <ChevronRight
                      size={18}
                      className="text-gray-500 transition-transform group-hover:translate-x-1"
                    />
                  </h2>
                  <p className="mt-1 text-sm text-gray-400">
                    KI-Artikel prüfen &amp; Quellen verwalten
                  </p>
                </CardBody>
              </Card>
            </RouterLink>

            {/* Aktive Kachel: Pro-Turniere (KI-Sync) */}
            <RouterLink to="/admin/pro-turniere" className="group">
              <Card
                isPressable={false}
                className="h-full w-full bg-zinc-900 border border-zinc-800 transition-colors group-hover:border-[#3fced1]/60"
              >
                <CardBody className="p-5">
                  <span
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl"
                    style={{ backgroundColor: `${ACCENT}1A`, color: ACCENT }}
                  >
                    <Trophy size={22} />
                  </span>
                  <h2 className="mt-4 text-lg font-bold text-white flex items-center gap-1">
                    PDC-Turniere
                    <ChevronRight
                      size={18}
                      className="text-gray-500 transition-transform group-hover:translate-x-1"
                    />
                  </h2>
                  <p className="mt-1 text-sm text-gray-400">
                    Turniere anlegen – die KI pflegt Daten &amp; Banner
                  </p>
                </CardBody>
              </Card>
            </RouterLink>

            {/* Aktive Kachel: Ranglisten (KI-Sync) */}
            <RouterLink to="/admin/ranglisten" className="group">
              <Card
                isPressable={false}
                className="h-full w-full bg-zinc-900 border border-zinc-800 transition-colors group-hover:border-[#3fced1]/60"
              >
                <CardBody className="p-5">
                  <span
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl"
                    style={{ backgroundColor: `${ACCENT}1A`, color: ACCENT }}
                  >
                    <BarChart3 size={22} />
                  </span>
                  <h2 className="mt-4 text-lg font-bold text-white flex items-center gap-1">
                    Ranglisten
                    <ChevronRight
                      size={18}
                      className="text-gray-500 transition-transform group-hover:translate-x-1"
                    />
                  </h2>
                  <p className="mt-1 text-sm text-gray-400">
                    Ranglisten anlegen – die KI füllt Tabelle &amp; Infos
                  </p>
                </CardBody>
              </Card>
            </RouterLink>

            {/* Aktive Kachel: Info-Seiten (KI-Sync) */}
            <RouterLink to="/admin/info-seiten" className="group">
              <Card
                isPressable={false}
                className="h-full w-full bg-zinc-900 border border-zinc-800 transition-colors group-hover:border-[#3fced1]/60"
              >
                <CardBody className="p-5">
                  <span
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl"
                    style={{ backgroundColor: `${ACCENT}1A`, color: ACCENT }}
                  >
                    <BookOpen size={22} />
                  </span>
                  <h2 className="mt-4 text-lg font-bold text-white flex items-center gap-1">
                    Info-Seiten
                    <ChevronRight
                      size={18}
                      className="text-gray-500 transition-transform group-hover:translate-x-1"
                    />
                  </h2>
                  <p className="mt-1 text-sm text-gray-400">
                    Darts-Wissen anlegen – KI schreibt Inhalt &amp; Banner
                  </p>
                </CardBody>
              </Card>
            </RouterLink>

            {/* Aktive Kachel: Tippspiele */}
            <RouterLink to="/admin/tippspiele" className="group">
              <Card
                isPressable={false}
                className="h-full w-full bg-zinc-900 border border-zinc-800 transition-colors group-hover:border-[#3fced1]/60"
              >
                <CardBody className="p-5">
                  <span
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl"
                    style={{ backgroundColor: `${ACCENT}1A`, color: ACCENT }}
                  >
                    <Dices size={22} />
                  </span>
                  <h2 className="mt-4 text-lg font-bold text-white flex items-center gap-1">
                    Tippspiele
                    <ChevronRight
                      size={18}
                      className="text-gray-500 transition-transform group-hover:translate-x-1"
                    />
                  </h2>
                  <p className="mt-1 text-sm text-gray-400">
                    Tippspiele anlegen, verwalten &amp; auswerten
                  </p>
                </CardBody>
              </Card>
            </RouterLink>

            {/* Aktive Kachel: Seiten (CMS) */}
            <RouterLink to="/admin/seiten" className="group">
              <Card
                isPressable={false}
                className="h-full w-full bg-zinc-900 border border-zinc-800 transition-colors group-hover:border-[#3fced1]/60"
              >
                <CardBody className="p-5">
                  <span
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl"
                    style={{ backgroundColor: `${ACCENT}1A`, color: ACCENT }}
                  >
                    <FileText size={22} />
                  </span>
                  <h2 className="mt-4 text-lg font-bold text-white flex items-center gap-1">
                    Seiten
                    <ChevronRight
                      size={18}
                      className="text-gray-500 transition-transform group-hover:translate-x-1"
                    />
                  </h2>
                  <p className="mt-1 text-sm text-gray-400">
                    Impressum, Datenschutz &amp; weitere Seiten bearbeiten
                  </p>
                </CardBody>
              </Card>
            </RouterLink>

            {/* Aktive Kachel: Slider */}
            <RouterLink to="/admin/slider" className="group">
              <Card
                isPressable={false}
                className="h-full w-full bg-zinc-900 border border-zinc-800 transition-colors group-hover:border-[#3fced1]/60"
              >
                <CardBody className="p-5">
                  <span
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl"
                    style={{ backgroundColor: `${ACCENT}1A`, color: ACCENT }}
                  >
                    <Sliders size={22} />
                  </span>
                  <h2 className="mt-4 text-lg font-bold text-white flex items-center gap-1">
                    Slider
                    <ChevronRight
                      size={18}
                      className="text-gray-500 transition-transform group-hover:translate-x-1"
                    />
                  </h2>
                  <p className="mt-1 text-sm text-gray-400">
                    Werbe- &amp; Hero-Slider mit Slides verwalten
                  </p>
                </CardBody>
              </Card>
            </RouterLink>

            {/* Deaktivierte Platzhalter */}
            {placeholderModules.map((module) => {
              const Icon = module.icon;
              return (
                <Card
                  key={module.name}
                  className="h-full bg-zinc-900/50 border border-zinc-800 opacity-60 cursor-not-allowed"
                >
                  <CardBody className="p-5">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-zinc-800 text-gray-500">
                      <Icon size={22} />
                    </span>
                    <h2 className="mt-4 text-lg font-bold text-gray-400">
                      {module.name}
                    </h2>
                    <p className="mt-1 text-sm text-gray-500">{module.description}</p>
                    <p className="mt-3 inline-flex items-center gap-1.5 text-xs text-gray-500">
                      <Smartphone size={13} />
                      In der DartCloud-App verfügbar
                    </p>
                  </CardBody>
                </Card>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminDashboardPage;
